
import React from 'react';
import { CategoryFilteredSpellList } from '../shared/CategoryFilteredSpellList';
import { VaultContents } from '../shared/VaultContents';
import { WarbandMemberCard } from '../shared/WarbandMemberCard';
import { useWarbandStore } from '../../stores/warbandStore';
import { useItemPopover } from '../../hooks/useItemPopover';
import { playWarbandService } from '../../services/playWarbandService';
import { batchUpdateService } from '../../services/batchUpdateService';
import { warbandService } from '../../services/warbandService';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { BatchUpdateRequest, BuffOrDebuff, PermanentInjury, SoldierDTO, SoldierUpdate } from '../../types';
import Box from '@mui/material/Box';
import { Button, Card, CardContent, CardHeader, Checkbox, Chip, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { getSplitStatValue } from '../../utils/splitStats';
import { computeLevelUpStatBonuses } from '../../utils/formatting';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { RemoveWizardModal } from '../modals/RemoveWizardModal';
import { SelectPermanentInjuryModal } from '../modals/SelectPermanentInjuryModal';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import PersonalInjuryIcon from '@mui/icons-material/PersonalInjury';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { gridFlexSx, treasureEffect } from '../../App';
import { createIceSurface, createSubtleIceSurface } from '../../theme/surfaces';
import { SpellSchoolChip } from '../shared/SpellSchoolChip';
import { SectionHeading } from '../shared/SectionHeading';
import { resetRefreshableItemUses } from '../../services/localStorageService';

type KoAction = null | { member: any; action: 'recover' | 'dead' | 'badlyWounded' | 'permanentInjury'; injury?: PermanentInjury };

export const PostGamePage: React.FC = () => {
  const { navigateToPage } = useWarbandStore();
  const selectedWarbandId = useWarbandStore((s) => s.selectedWarbandId);
  const wizardData = useWarbandStore((s) => s.wizardData);
  const apprenticeData = useWarbandStore((s) => s.apprenticeData);
  const soldiersData = useWarbandStore((s) => s.soldiersData);
  const { handleItemClick } = useItemPopover();

  // Collect all warband members (wizard, apprentice, soldiers)
  const allMembers = React.useMemo(() => [
    ...(wizardData ? [wizardData] : []),
    ...(apprenticeData ? [apprenticeData] : []),
    ...(soldiersData?.soldiers ?? []),
  ], [wizardData, apprenticeData, soldiersData]);


  // Filter for members with 0 hitpoints
  const koMembers = allMembers.filter((m) => m.currentHealth === 0 && !m.isTemporary);
  const treasureMembers = allMembers.filter((m) => m.carryingTreasure && m.leftTable);

  // Dialog state
  const [confirmAction, setConfirmAction] = React.useState<KoAction>(null);
  const [confirmCleanup, setConfirmCleanup] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [wizardDeathModalOpen, setWizardDeathModalOpen] = React.useState(false);
  const [retainDeathVaultItems, setRetainDeathVaultItems] = React.useState(false);
  const [loseAllItems, setLoseAllItems] = React.useState(false);
  const [injuryModalMember, setInjuryModalMember] = React.useState<any>(null);

  // Store actions for updating/removing members
  const setSoldiersData = useWarbandStore((s) => s.setSoldiersData);
  const setWizardData = useWarbandStore((s) => s.setWizardData);
  const setApprenticeData = useWarbandStore((s) => s.setApprenticeData);

  const { queueWizardDelete, queueSoldierDelete, queueApprenticeDelete, toggleMemberBadlyWounded, updateMemberTreasureItems, updateVaultItems, updateMemberPermanentInjuries } = useDirtyTrackingStore();
  const vaultItems = useWarbandStore((s) => s.vaultItems);
  const setSpellcastersAndSpells = useWarbandStore((s) => s.setSpellcastersAndSpells);
  const setWarbandBuffs = useWarbandStore((s) => s.setWarbandBuffs);
  const warbands = useWarbandStore((s) => s.warbands);
  const setWarbands = useWarbandStore((s) => s.setWarbands);
  const selectedWarband = warbands.find((w) => w.id === selectedWarbandId) ?? null;

  const [xpToAdd, setXpToAdd] = React.useState<number>(0);
  const [xpBusy, setXpBusy] = React.useState(false);
  const [xpError, setXpError] = React.useState<string | null>(null);

  const [goldToAdd, setGoldToAdd] = React.useState<number>(0);
  const [goldBusy, setGoldBusy] = React.useState(false);
  const [goldError, setGoldError] = React.useState<string | null>(null);

  const levelUps = useWarbandStore((s) => s.levelUps);
  const healthBonus = React.useMemo(() => computeLevelUpStatBonuses(levelUps).health, [levelUps]);

  const handleAddXp = async () => {
    if (!selectedWarband || xpToAdd <= 0) return;
    setXpBusy(true);
    setXpError(null);
    try {
      const newExperience = selectedWarband.experience + xpToAdd;
      await warbandService.update(selectedWarband.id, { ...selectedWarband, experience: newExperience });
      setWarbands(warbands.map((w) => (w.id === selectedWarband.id ? { ...w, experience: newExperience } : w)));
      setXpToAdd(0);
    } catch {
      setXpError('Failed to update experience. Please try again.');
    } finally {
      setXpBusy(false);
    }
  };

  const handleAddGold = async () => {
    if (!selectedWarband || goldToAdd <= 0) return;
    setGoldBusy(true);
    setGoldError(null);
    try {
      const newGold = selectedWarband.gold + goldToAdd;
      await warbandService.update(selectedWarband.id, { ...selectedWarband, gold: newGold });
      setWarbands(warbands.map((w) => (w.id === selectedWarband.id ? { ...w, gold: newGold } : w)));
      setGoldToAdd(0);
    } catch {
      setGoldError('Failed to update gold. Please try again.');
    } finally {
      setGoldBusy(false);
    }
  };

  const clearBuffsIfNeeded = (memberId: string, buffs: BuffOrDebuff[] | undefined) =>
    (buffs?.length ?? 0) > 0
      ? playWarbandService.replaceMemberBuffs(selectedWarbandId!, memberId, [])
      : Promise.resolve(null);

  const handleGameCleanUp = async () => {
    if (!selectedWarbandId) return;
    setBusy(true);
    try {
      const soldiers = soldiersData?.soldiers ?? [];

      // Clear buffs server-side for members that have them.
      await Promise.all([
        wizardData ? clearBuffsIfNeeded(wizardData.id, wizardData.buffsAndDebuffs) : Promise.resolve(null),
        apprenticeData ? clearBuffsIfNeeded(apprenticeData.id, apprenticeData.buffsAndDebuffs) : Promise.resolve(null),
        ...soldiers.map((s) => clearBuffsIfNeeded(s.id, s.buffsAndDebuffs)),
      ]);

      // Build a batch update request that heals every member to full health.
      // Strip temporary buffsAndDebuffs (already cleared server-side above) so only
      // permanent injuries affect the calculated max health.
      const request: BatchUpdateRequest = {};
      if (wizardData) {
        request.wizard = {
          currentHealth: getSplitStatValue({ ...wizardData, buffsAndDebuffs: [] }, 'health', wizardData.health + healthBonus),
          carryingTreasure: false,
          leftTable: false,
        };
      }
      if (apprenticeData) {
        request.apprentice = {
          currentHealth: getSplitStatValue({ ...apprenticeData, buffsAndDebuffs: [] }, 'health', apprenticeData.health + healthBonus),
          carryingTreasure: false,
          leftTable: false,
        };
      }
      if (soldiers.length > 0) {
        request.soldiers = soldiers.map<SoldierUpdate>((s) => ({
          id: s.id,
          currentHealth: getSplitStatValue({ ...s, buffsAndDebuffs: [] }, 'health', s.health + healthBonus),
          carryingTreasure: false,
          leftTable: false,
        }));
      }

      // Clear all warband-level buffs as part of cleanup
      request.warbandBuffIds = [];

      // Send via the standard batch-update endpoint and receive the
      // authoritative post-heal warband state.
      const response = await batchUpdateService.batchUpdate(selectedWarbandId, request);
      const data = response.data;

      // Write the server-authoritative state back to the stores. Clear
      // buffsAndDebuffs locally since we already cleared them above.
      if (data.wizard) {
        setWizardData({ ...data.wizard, buffsAndDebuffs: [] });
      }
      setApprenticeData(data.apprentice ? { ...data.apprentice, buffsAndDebuffs: [] } : null);
      if (data.soldiers) {
        setSoldiersData({
          soldiers: data.soldiers.soldiers
            .filter((s) => !s.isTemporary)
            .map((s) => ({ ...s, buffsAndDebuffs: [] })),
        });
      }
      setSpellcastersAndSpells({
        wizard: data.wizard ? { ...data.wizard, buffsAndDebuffs: [] } : null,
        apprentice: data.apprentice ? { ...data.apprentice, buffsAndDebuffs: [] } : null,
        warbandSpells: data.warbandSpells ?? [],
      });
      setWarbandBuffs([]);

      // Reset uses for items that refresh between games.
      resetRefreshableItemUses(selectedWarbandId);

      navigateToPage('pregame');
      navigateToPage('manage');
    } finally {
      setBusy(false);
    }
  };


  const membersWounded = React.useMemo(() => {
    return allMembers.some((m) => m.currentHealth <= 0 && !m.isTemporary);
  }, [allMembers]);

  const membersNeedHealing = React.useMemo(() => {
    return allMembers.some((m) => m.currentHealth < getSplitStatValue(m, 'health', m.health + healthBonus) && !m.isTemporary);
  }, [allMembers, healthBonus]);

  const memberHaveBuffs = React.useMemo(() => {
    return allMembers.some((m) => (m.buffsAndDebuffs?.length ?? 0) > 0 && !m.isTemporary);
  }, [allMembers]);

  const memberHaveTreasure = React.useMemo(() => {
    return allMembers.some((m) => m.carryingTreasure);
  }, [allMembers]);

  const memberLeftTable = React.useMemo(() => {
    return allMembers.some((m) => m.leftTable);
  }, [allMembers]);

  const membersTemporaryPresent = React.useMemo(() => {
    return allMembers.some((m) => m.isTemporary);
  }, [allMembers]);



  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0, overflowY: 'auto', pt: 2, pb: 2 }}>
      {koMembers.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...createIceSurface(41, { icicles: true, card: true }), p: 1.5 }}>
          <SectionHeading label="Wounded & KO'd Members" />
          <Box sx={gridFlexSx}>
            {koMembers.map((member) => (
              <WarbandMemberCard
                key={member.id}
                member={member}
                isDefeated={true}
                showArmourWhenEmpty={(member.creatureId !== "wizard" && member.creatureId !== "apprentice")}
                onItemClick={handleItemClick}
                bottomRightActionContent={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', gap: 1 }}>
                    <Button
                      startIcon={<AccessibilityIcon />}
                      size="small"
                      aria-label="dead"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (wizardData && member.id === wizardData.id) {
                          setWizardDeathModalOpen(true);
                        } else {
                          setConfirmAction({ member, action: 'dead' });
                        }
                      }}
                    >
                      Death
                    </Button>
                    {(wizardData === member || apprenticeData === member) && (
                      <Button
                        startIcon={<HeartBrokenIcon />}
                        size="small"
                        aria-label="permanent injury"
                        variant="contained"
                        color="error"
                        onClick={() => setInjuryModalMember(member)}
                      >
                        Permanent Injury
                      </Button>
                    )}
                    <Button
                      startIcon={<PersonalInjuryIcon />}
                      size="small"
                      aria-label="badly wounded"
                      variant="contained"
                      color="primary"
                      onClick={() => setConfirmAction({ member, action: 'badlyWounded' })}
                    >
                      Badly Wounded
                    </Button>
                    <Button
                      startIcon={<FavoriteIcon />}
                      size="small"
                      aria-label="full recovery"
                      variant="contained"
                      color="primary"
                      onClick={() => setConfirmAction({ member, action: 'recover' })}
                    >
                      Full Recovery
                    </Button>
                  </Box>
                }
              />
            ))}
          </Box>
        </Box>
      )}


      {treasureMembers.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...createIceSurface(42, { icicles: true, card: true }), p: 1.5 }}>
          <SectionHeading label="Treasure Captured This Game" />
          <Box sx={{ textAlign: 'center', py: 3, ...createSubtleIceSurface(501, {}) }}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {treasureMembers.map((member) => (
                <Box sx={{ width: 160 }}>
                  <Button
                    key={member.id}
                    aria-label=" treasure"
                    sx={{
                      color: '#EDD270',
                      height: '60px',
                      width: '140px',
                      '& .MuiChip-label': {
                        display: 'block',
                        whiteSpace: 'normal',
                      },
                      ...treasureEffect
                    }}
                  >
                    {member.name}
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...createIceSurface(42, { icicles: true, card: true }), p: 1.5 }}>
        <CategoryFilteredSpellList categoryIds={["out_of_game_a"]} label="Out Of Game (A) Spells" />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...createIceSurface(42, { icicles: true, card: true }), p: 1.5 }}>
        <VaultContents inlineAddButton />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...createIceSurface(42, { icicles: true, card: true }), p: 1.5 }}>
        <SpellSchoolChip
          school={selectedWarband?.schoolId ?? 'Unknown School'}
          size="medium"
          label={`Current XP: ${selectedWarband?.experience ?? 0}`}
        />
        <TextField
          label="XP Earned This Game"
          type="number"
          value={xpToAdd}
          onChange={(e) => {
            setXpError(null);
            setXpToAdd(Math.max(0, parseInt(e.target.value, 10) || 0));
          }}
          slotProps={{ htmlInput: { min: 0 } }}
          error={!!xpError}
          helperText={xpError ?? undefined}
          disabled={xpBusy || !selectedWarband}
          sx={{ maxWidth: 240 }}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={xpBusy || xpToAdd <= 0 || !selectedWarband}
          onClick={handleAddXp}
          sx={{ width: 'fit-content' }}
        >
          Add XP
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...createIceSurface(42, { icicles: true, card: true }), p: 1.5 }}>
        <Stack direction="column" spacing={1} alignItems="flex-start" justifyContent="flex-start">
          <Chip
            label={`Current Gold: ${selectedWarband?.gold ?? 0} gc`}
            sx={{ ...treasureEffect, color: '#EDD270', cursor: 'pointer' }}
          />
        </Stack>
        <TextField
          label="Gold Earned This Game"
          type="number"
          value={goldToAdd}
          onChange={(e) => {
            setGoldError(null);
            setGoldToAdd(Math.max(0, parseInt(e.target.value, 10) || 0));
          }}
          slotProps={{ htmlInput: { min: 0 } }}
          error={!!goldError}
          helperText={goldError ?? undefined}
          disabled={goldBusy || !selectedWarband}
          sx={{ maxWidth: 240 }}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={goldBusy || goldToAdd <= 0 || !selectedWarband}
          onClick={handleAddGold}
          sx={{ width: 'fit-content' }}
        >
          Add Gold
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...createIceSurface(42, { icicles: true, card: true }), p: 1.5 }}>
        <SectionHeading label="End Of Game Cleanup" />
        {membersWounded && (
          <Typography>Some members are KO'd and need to be attended to.</Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<DoneAllIcon />}
          disabled={busy || !selectedWarbandId || membersWounded}
          onClick={() => setConfirmCleanup(true)}
        >
          End game
        </Button>
      </Box>

      {/* ConfirmActionDialog for end-of-game cleanup */}
      <ConfirmActionDialog
        open={confirmCleanup}
        title="End Game?"
        message="This will heal all members to full health, clear all statuses, remove temporary soldiers, and reset treasure. Are you sure?"
        confirmLabel="End Game"
        confirmColor="primary"
        busy={busy}
        onClose={() => !busy && setConfirmCleanup(false)}
        onConfirm={async () => {
          await handleGameCleanUp();
          setConfirmCleanup(false);
        }}
      />

      {/* ConfirmActionDialog for KO actions */}
      <ConfirmActionDialog
        open={!!confirmAction}
        title={
          confirmAction?.action === 'recover'
            ? 'Are you sure about this Full Recovery?'
            : confirmAction?.action === 'dead'
              ? 'Are you sure about this Death?'
              : confirmAction?.action === 'badlyWounded'
                ? 'Mark as Badly Wounded?'
                : confirmAction?.action === 'permanentInjury'
                  ? `Apply Permanent Injury: ${confirmAction.injury?.name}?`
                  : ''
        }
        message={
          confirmAction?.action === 'recover'
            ? `Are you sure you want to fully heal ${confirmAction.member?.name}?`
            : confirmAction?.action === 'dead'
              ? `Are you sure you want to remove ${confirmAction.member?.name} from the party? This cannot be undone.`
              : confirmAction?.action === 'badlyWounded'
                ? `Mark ${confirmAction.member?.name} as Badly Wounded? They will sit out the next game.`
                : confirmAction?.action === 'permanentInjury'
                  ? `Apply "${confirmAction.injury?.name}" to ${confirmAction.member?.name}? They will be healed to full health but carry this injury permanently.`
                  : ''
        }
        confirmLabel={
          confirmAction?.action === 'recover' ? 'Heal to Full'
            : confirmAction?.action === 'badlyWounded' ? 'Badly Wounded'
              : confirmAction?.action === 'permanentInjury' ? 'Apply Injury'
                : 'Remove'
        }
        confirmColor={confirmAction?.action === 'dead' ? 'error' : confirmAction?.action === 'permanentInjury' ? 'error' : 'primary'}
        busy={busy}
        onClose={() => {
          if (!busy) {
            setConfirmAction(null);
            setRetainDeathVaultItems(false);
            setLoseAllItems(false);
          }
        }}
        onConfirm={async () => {
          if (!confirmAction) return;
          setBusy(true);
          const member = confirmAction.member;
          const action = confirmAction.action;

          // Removes one vault copy of each item the member carries, then clears their items.
          const applyClosingCall = async (memberId: string, memberRole: 'wizard' | 'apprentice' | 'soldier') => {
            const memberItemIds: string[] = (member.treasureItems ?? []).map((t: any) => t.id);
            const remaining = [...vaultItems];
            for (const id of memberItemIds) {
              const idx = remaining.findIndex((v) => v.id === id);
              if (idx !== -1) remaining.splice(idx, 1);
            }
            await updateMemberTreasureItems(memberId, memberRole, []);
            await updateVaultItems(remaining.map((v) => v.id));
          };

          try {
            if (action === 'recover') {
              // Heal member to full (accounting for permanent injuries and level-up bonuses)
              if ('type' in member && soldiersData) {
                // Soldier
                setSoldiersData({
                  soldiers: soldiersData.soldiers.map((s) =>
                    s.id === member.id ? { ...s, currentHealth: getSplitStatValue(s, 'health', s.health + healthBonus) } : s
                  ),
                });
                if (loseAllItems) await applyClosingCall(member.id, 'soldier');
              } else if (wizardData && member.id === wizardData.id) {
                setWizardData({ ...wizardData, currentHealth: getSplitStatValue(wizardData, 'health', wizardData.health + healthBonus) });
                if (loseAllItems) await applyClosingCall(wizardData.id, 'wizard');
              } else if (apprenticeData && member.id === apprenticeData.id) {
                setApprenticeData({ ...apprenticeData, currentHealth: getSplitStatValue(apprenticeData, 'health', apprenticeData.health + healthBonus) });
                if (loseAllItems) await applyClosingCall(apprenticeData.id, 'apprentice');
              }
            } else if (action === 'badlyWounded') {
              // Restore health to full (accounting for permanent injuries and level-up bonuses) and mark as badly wounded
              if ('type' in member && soldiersData) {
                setSoldiersData({
                  soldiers: soldiersData.soldiers.map((s) =>
                    s.id === member.id ? { ...s, currentHealth: getSplitStatValue(s, 'health', s.health + healthBonus) } : s
                  ),
                });
                toggleMemberBadlyWounded(member.id, 'soldier');
              } else if (wizardData && member.id === wizardData.id) {
                setWizardData({ ...wizardData, currentHealth: getSplitStatValue(wizardData, 'health', wizardData.health + healthBonus) });
                toggleMemberBadlyWounded(wizardData.id, 'wizard');
              } else if (apprenticeData && member.id === apprenticeData.id) {
                setApprenticeData({ ...apprenticeData, currentHealth: getSplitStatValue(apprenticeData, 'health', apprenticeData.health + healthBonus) });
                toggleMemberBadlyWounded(apprenticeData.id, 'apprentice');
              }
            } else if (action === 'permanentInjury' && confirmAction.injury) {
              const injury = confirmAction.injury;
              // Heal to full (accounting for existing permanent injuries and level-up bonuses) and add the new permanent injury
              if (wizardData && member.id === wizardData.id) {
                const existingIds = (wizardData.permanentInjuries ?? []).map((i) => i.id);
                setWizardData({ ...wizardData, currentHealth: getSplitStatValue(wizardData, 'health', wizardData.health + healthBonus) });
                updateMemberPermanentInjuries(wizardData.id, 'wizard', [...existingIds, injury.id]);
              } else if (apprenticeData && member.id === apprenticeData.id) {
                const existingIds = (apprenticeData.permanentInjuries ?? []).map((i) => i.id);
                setApprenticeData({ ...apprenticeData, currentHealth: getSplitStatValue(apprenticeData, 'health', apprenticeData.health + healthBonus) });
                updateMemberPermanentInjuries(apprenticeData.id, 'apprentice', [...existingIds, injury.id]);
              }
            } else if (action === 'dead') {
              // Remove member
              if ('type' in member && soldiersData) {
                // Soldier
                queueSoldierDelete((member as SoldierDTO).id, (member as SoldierDTO).cost, false, retainDeathVaultItems);
              } else if (wizardData && member.id === wizardData.id) {
                setWizardData(null);
              } else if (apprenticeData && member.id === apprenticeData.id) {
                queueApprenticeDelete(retainDeathVaultItems);
              }
            }
            setConfirmAction(null);
            setRetainDeathVaultItems(false);
            setLoseAllItems(false);
          } finally {
            setBusy(false);
          }
        }}
      >
        {confirmAction?.action === 'recover' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={loseAllItems}
                onChange={(e) => setLoseAllItems(e.target.checked)}
                size="small"
                disabled={busy}
              />
            }
            label={<Typography variant="body2">Close call — lose all items</Typography>}
            sx={{ mt: 1 }}
          />
        )}
        {confirmAction?.action === 'dead' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={retainDeathVaultItems}
                onChange={(e) => setRetainDeathVaultItems(e.target.checked)}
                size="small"
                disabled={busy}
              />
            }
            label={<Typography variant="body2">Keep equipment in vault</Typography>}
            sx={{ mt: 1 }}
          />
        )}
      </ConfirmActionDialog>

      <RemoveWizardModal
        open={wizardDeathModalOpen}
        hasApprentice={!!apprenticeData}
        busy={false}
        retainVaultItems={retainDeathVaultItems}
        onRetainVaultItemsChange={setRetainDeathVaultItems}
        onClose={() => {
          setWizardDeathModalOpen(false);
          setRetainDeathVaultItems(false);
        }}
        onConfirmDelete={() => {
          queueWizardDelete(false, retainDeathVaultItems);
          setWizardDeathModalOpen(false);
          setRetainDeathVaultItems(false);
        }}
        onConfirmDeleteAndPromote={() => {
          queueWizardDelete(true, retainDeathVaultItems);
          setWizardDeathModalOpen(false);
          setRetainDeathVaultItems(false);
        }}
      />

      <SelectPermanentInjuryModal
        open={!!injuryModalMember}
        member={injuryModalMember}
        onClose={() => setInjuryModalMember(null)}
        onSelect={(injury) => {
          setConfirmAction({ member: injuryModalMember, action: 'permanentInjury', injury });
          setInjuryModalMember(null);
        }}
      />
    </Box>
  );
};
